import { CARRIER_COORDINATION as T } from "@/constants/testIds";
import {
  FileText, Send, BadgeCheck, Container as ContainerIcon,
  ScrollText, Ship, Clock3, PackageCheck,
} from "lucide-react";

const steps = [
  { icon: FileText, title: "Purchase Order", detail: "PO created and shared with the workspace." },
  { icon: Send, title: "Request Booking", detail: "Ocean freight booking request sent to carriers." },
  { icon: BadgeCheck, title: "Carrier Confirmation", detail: "Selected carrier confirms sailing and cutoff." },
  { icon: ContainerIcon, title: "Container Allocation", detail: "Container numbers and release details are logged." },
  { icon: ScrollText, title: "Shipping Documents", detail: "B/L, invoice and COO prepared and tracked." },
  { icon: Ship, title: "Vessel Departure", detail: "Departure confirmed. Real-time status begins." },
  { icon: Clock3, title: "ETA Updates", detail: "ETA changes flow into one shared timeline." },
  { icon: PackageCheck, title: "Delivered", detail: "Shipment closes with a complete audit trail." },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid={T.howItWorks} className="relative border-t border-slate-line bg-white py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-brand">The Workflow</p>
          <h2 className="mt-4 text-3xl font-semibold text-navy sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
            How FreightIQ™ Works
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-slate-600">
            A connected, linear flow that moves every shipment from purchase order to delivery — with carrier coordination handled in one place.
          </p>
        </div>

        <div className="relative mt-16 hidden lg:block">
          <div className="absolute left-0 right-0 top-[38px] mx-auto h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
          <ol className="relative grid grid-cols-4 gap-x-6 gap-y-16">
            {steps.map(({ icon: Icon, title, detail }, i) => (
              <li key={title} className={`animate-fade-up stagger-${(i % 6) + 1} flex flex-col items-center text-center`}>
                <span className="relative z-10 flex h-[76px] w-[76px] items-center justify-center rounded-2xl border border-slate-line bg-white shadow-card">
                  <Icon className="h-7 w-7 text-blue-brand" strokeWidth={1.6} />
                  <span
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-[11px] font-semibold text-white"
                    style={{ backgroundColor: "hsl(var(--navy))" }}
                  >
                    {i + 1}
                  </span>
                </span>
                <h3 className="mt-5 text-[15px] font-semibold text-navy">{title}</h3>
                <p className="mt-1.5 max-w-[220px] text-[13px] leading-relaxed text-slate-500">{detail}</p>
              </li>
            ))}
          </ol>
        </div>

        <ol className="relative mt-14 space-y-6 lg:hidden">
          <div className="absolute bottom-0 left-[35px] top-0 w-px bg-slate-200" />
          {steps.map(({ icon: Icon, title, detail }, i) => (
            <li key={title} className="relative flex gap-5">
              <span className="relative z-10 flex h-[70px] w-[70px] shrink-0 items-center justify-center rounded-2xl border border-slate-line bg-white shadow-card">
                <Icon className="h-6 w-6 text-blue-brand" strokeWidth={1.6} />
                <span
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-[11px] font-semibold text-white"
                  style={{ backgroundColor: "hsl(var(--navy))" }}
                >
                  {i + 1}
                </span>
              </span>
              <div className="pt-2">
                <h3 className="text-[15px] font-semibold text-navy">{title}</h3>
                <p className="mt-1 text-[13.5px] leading-relaxed text-slate-500">{detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
